import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { copyToClipboard } from '../utils/clipboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CheckCircle, AlertCircle, ExternalLink, Copy, Check, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function Office365Setup() {
  const [credentials, setCredentials] = useState({
    client_id: '',
    tenant_id: 'common',
    client_secret: '',
    sso_mode: 'multi_tenant'  // 'single_tenant' or 'multi_tenant'
  });
  const [hasExistingSecret, setHasExistingSecret] = useState(false);
  const [secretTouched, setSecretTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const redirectUri = `${window.location.origin}/api/oauth/callback`;

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await api.post('/office365/test-connection', credentials);
      setTestResult(response.data);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Connection test failed'
      });
      toast.error('Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  // Load existing configuration from database
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/office365/config');
      if (response.data.client_id) {
        setCredentials({
          client_id: response.data.client_id,
          tenant_id: response.data.tenant_id,
          client_secret: response.data.has_secret ? '****************************************' : '',
          sso_mode: response.data.sso_mode || (response.data.tenant_id === 'common' || response.data.tenant_id === 'organizations' ? 'multi_tenant' : 'single_tenant')
        });
        setHasExistingSecret(response.data.has_secret);
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    }
  };


  const handleSave = async () => {
    setSaving(true);
    try {
      const dataToSend = { ...credentials };
      
      // If secret wasn't touched and we have existing secret, don't send it
      if (hasExistingSecret && !secretTouched) {
        delete dataToSend.client_secret;
      }
      
      await api.post('/office365/config', dataToSend);
      toast.success('Office 365 configuration saved successfully');
      setSecretTouched(false);
      loadConfig(); // Reload to show masked secret
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToClipboard = async (text) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied to clipboard');
    } else {
      toast.error('Failed to copy. Please copy manually: ' + text);
    }
  };

  return (
    <div className="co-page co-scope co-settings space-y-6" data-testid="office365-setup-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Office 365 Setup Guide</h1>
        <p className="text-gray-600 mt-1">Configure Microsoft Office 365 email integration</p>
      </div>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Before you begin:</strong> You need a Microsoft 365 account with admin access to register an application.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="guide" className="space-y-4">
        <TabsList>
          <TabsTrigger value="guide">Setup Guide</TabsTrigger>
          <TabsTrigger value="config">Configure Credentials</TabsTrigger>
        </TabsList>

        <TabsContent value="guide" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Step 1: Register Application in Azure Portal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <ol className="list-decimal list-inside space-y-3">
                <li>
                  Go to{' '}
                  <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                    Azure Portal
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
                <li>Search for and select <strong>"App registrations"</strong></li>
                <li>Click <strong>"+ New registration"</strong></li>
                <li>Enter application details:
                  <ul className="ml-6 mt-2 space-y-1">
                    <li>Name: <code className="bg-gray-100 px-2 py-1 rounded">CipherOne Email Integration</code></li>
                    <li>Supported account types: <strong>{credentials.sso_mode === 'multi_tenant' ? 'Accounts in any organizational directory (Any Microsoft Entra ID tenant - Multitenant)' : 'Single tenant (this org only)'}</strong></li>
                    <li>Redirect URI: 
                      <div className="flex items-center gap-2 mt-1">
                        <code className="bg-gray-100 px-3 py-1 rounded text-sm flex-1">{redirectUri}</code>
                        <Button size="sm" variant="outline" onClick={() => handleCopyToClipboard(redirectUri)}>
                          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </div>
                    </li>
                  </ul>
                </li>
                <li>Click <strong>"Register"</strong></li>
              </ol>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Step 2: Record Application Credentials</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Application (client) ID</Label>
                <p className="text-sm text-gray-600">Copy this from the "Overview" page</p>
              </div>
              <div>
                <Label>Directory (tenant) ID</Label>
                <p className="text-sm text-gray-600">Also on the "Overview" page</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Step 3: Create Client Secret</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-2">
                <li>Go to <strong>"Certificates & secrets"</strong></li>
                <li>Click <strong>"+ New client secret"</strong></li>
                <li>Add description: "CipherOne Integration"</li>
                <li>Choose expiration: 24 months recommended</li>
                <li>Click <strong>"Add"</strong></li>
                <li><strong className="text-red-600">IMPORTANT:</strong> Copy the secret <strong>Value</strong> immediately (not the ID)</li>
              </ol>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Step 4: Configure API Permissions</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="list-decimal list-inside space-y-2">
                <li>Go to <strong>"API permissions"</strong></li>
                <li>Click <strong>"+ Add a permission"</strong></li>
                <li>Select <strong>"Microsoft Graph"</strong></li>
                <li>Choose <strong>"Delegated permissions"</strong></li>
                <li>Add these permissions:
                  <ul className="ml-6 mt-2 space-y-1 text-sm">
                    <li>✓ Mail.Read</li>
                    <li>✓ Mail.Send</li>
                    <li>✓ User.Read</li>
                    <li>✓ offline_access</li>
                  </ul>
                </li>
                <li>Click <strong>"Grant admin consent"</strong></li>
                <li>Confirm by clicking <strong>"Yes"</strong></li>
              </ol>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Step 5: Update CipherOne Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Update backend/.env file:</strong> Add your Microsoft credentials to the .env file on the server.
                </AlertDescription>
              </Alert>
              <pre className="mt-3 bg-gray-900 text-green-400 p-4 rounded text-sm whitespace-pre-wrap break-all">
{`# Uncomment and update these lines in backend/.env:
MICROSOFT_CLIENT_ID="your-actual-client-id"
MICROSOFT_TENANT_ID="your-actual-tenant-id"  
MICROSOFT_CLIENT_SECRET="your-actual-client-secret"
MICROSOFT_REDIRECT_URI="${redirectUri}"`}
              </pre>
              <p className="text-sm text-gray-600 mt-3">
                After updating, restart the backend service: <code className="bg-gray-100 px-2 py-1 rounded">supervisorctl restart backend</code>
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="config">
          <Card>
            <CardHeader>
              <CardTitle>Quick Configuration</CardTitle>
              <CardDescription>Enter your Microsoft app credentials</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-blue-50 border-blue-200">
                <AlertCircle className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-900">
                  <strong>Note:</strong> These credentials must be manually added to <code>backend/.env</code> file and the backend restarted.
                </AlertDescription>
              </Alert>
              
              <div>
                <Label htmlFor="client_id">Application (client) ID</Label>
                <Input
                  id="client_id"
                  value={credentials.client_id}
                  onChange={(e) => setCredentials({...credentials, client_id: e.target.value})}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  className="font-mono"
                />
              </div>
              
              <div>
                <Label>SSO Account Type</Label>
                <Select
                  value={credentials.sso_mode}
                  onValueChange={(value) => {
                    const newTenant = value === 'multi_tenant' ? 'common' : credentials.tenant_id === 'common' ? '' : credentials.tenant_id;
                    setCredentials({...credentials, sso_mode: value, tenant_id: value === 'multi_tenant' ? 'common' : newTenant});
                  }}
                >
                  <SelectTrigger data-testid="sso-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="multi_tenant">Multi-tenant (All Organizations)</SelectItem>
                    <SelectItem value="single_tenant">Single Tenant (My Org Only)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-neutral-500 mt-1">
                  {credentials.sso_mode === 'multi_tenant' 
                    ? 'Any Microsoft 365 user from any organization can sign in. Set this in Azure AD > App Registration > Supported account types.'
                    : 'Only users from your specific Azure AD tenant can sign in.'}
                </p>
              </div>

              {credentials.sso_mode === 'single_tenant' && (
                <div>
                  <Label htmlFor="tenant_id">Directory (tenant) ID</Label>
                  <Input
                    id="tenant_id"
                    value={credentials.tenant_id}
                    onChange={(e) => setCredentials({...credentials, tenant_id: e.target.value})}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    className="font-mono"
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="client_secret">
                  Client Secret (Value)
                  {hasExistingSecret && !secretTouched && (
                    <span className="text-xs text-green-600 ml-2">✓ Already configured</span>
                  )}
                </Label>
                <Input
                  id="client_secret"
                  type="password"
                  value={credentials.client_secret}
                  onChange={(e) => {
                    setCredentials({...credentials, client_secret: e.target.value});
                    setSecretTouched(true);
                  }}
                  onFocus={(e) => {
                    // Clear masked value when user focuses
                    if (hasExistingSecret && !secretTouched) {
                      setCredentials({...credentials, client_secret: ''});
                      setSecretTouched(true);
                    }
                  }}
                  placeholder={hasExistingSecret && !secretTouched ? "Secret configured - click to change" : "Enter the secret Value (not ID)"}
                />
                {hasExistingSecret && !secretTouched && (
                  <p className="text-xs text-gray-500 mt-1">
                    Leave as-is to keep current secret, or click to enter a new one
                  </p>
                )}
              </div>

              <div>
                <Label>Redirect URI (configured in Azure)</Label>
                <div className="flex items-center gap-2">
                  <Input value={redirectUri} readOnly className="font-mono text-sm" />
                  <Button size="sm" variant="outline" onClick={() => handleCopyToClipboard(redirectUri)}>
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Test Result */}
              {testResult && (
                <Alert className={testResult.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}>
                  {testResult.success ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  )}
                  <AlertDescription className={testResult.success ? "text-green-900" : "text-red-900"}>
                    {testResult.message}
                  </AlertDescription>
                </Alert>
              )}

              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-900">
                  <strong>Save Configuration:</strong> Click the button below to save these credentials to the database. This will make Office 365 SSO and mailbox integration work immediately without requiring backend restart.
                </AlertDescription>
              </Alert>

              <div className="flex justify-between items-center pt-4 border-t gap-3">
                <div className="text-sm text-gray-600">
                  Test credentials before saving to verify they work.
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleTestConnection} 
                    disabled={testing || !credentials.client_id || !credentials.tenant_id || !credentials.client_secret}
                    variant="outline"
                    className="gap-2"
                  >
                    {testing ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Test Connection
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={handleSave} 
                    disabled={saving || !credentials.client_id || !credentials.tenant_id}
                    className="gap-2"
                  >
                    {saving ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Save Configuration
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <Alert className="bg-yellow-50 border-yellow-200">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-900">
                  <strong>Manual Configuration Required:</strong>
                  <pre className="mt-2 text-xs bg-gray-900 text-green-400 p-2 rounded whitespace-pre-wrap break-all">
{`# Add to backend/.env:
MICROSOFT_CLIENT_ID="${credentials.client_id || 'your-client-id'}"
MICROSOFT_TENANT_ID="${credentials.tenant_id || 'common'}"
MICROSOFT_CLIENT_SECRET="${credentials.client_secret || 'your-secret'}"
MICROSOFT_REDIRECT_URI="${redirectUri}"`}
                  </pre>
                  <p className="mt-2 text-sm">Then restart backend: <code className="bg-yellow-100 px-1 rounded">supervisorctl restart backend</code></p>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
